import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Phone, Mail, MapPin, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { COMPANY_ADDRESS } from "../mock";
import { submitLead, emptyLeadForm } from "../lib/api";

const Contact = () => {
  const [form, setForm] = useState(emptyLeadForm());
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | loading | success | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await submitLead({ ...form, source: "contact-page", formType: "contact" });
      setStatus({ state: "success", message: "Message sent! We’ll be in touch within 24 hours." });
      setForm(emptyLeadForm());
      setTimeout(() => setStatus({ state: "idle", message: "" }), 5000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message: typeof detail === "string" ? detail : "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header/>
      <PageHero eyebrow="GET IN TOUCH" title="Talk to a SkillsGain advisor" subtitle="We respond to every enterprise enquiry within 24 hours."/>
      <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-3 gap-8">
        <div className="space-y-5">
          <InfoCard icon={Phone} title="Call us" lines={["+91 80 4567 8900", "Mon-Sat, 9am to 7pm IST"]}/>
          <InfoCard icon={Mail} title="Email us" lines={["info@skillsgain.com", "sales@skillsgain.com"]}/>
          <InfoCard icon={MapPin} title="Visit us" lines={[COMPANY_ADDRESS.line1, COMPANY_ADDRESS.line2, COMPANY_ADDRESS.line3, COMPANY_ADDRESS.line4]}/>
        </div>

        <div className="lg:col-span-2 bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8">
          <h2 className="text-[#15348C]">Send us a message</h2>
          <p className="text-sm text-gray-500 mt-1">Our learning advisor will reach out soon.</p>
          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="contact-form">
            <Field label="Full Name" name="name" testid="contact-name" value={form.name} onChange={v => setForm({...form, name: v})} required/>
            <Field label="Work Email" name="email" testid="contact-email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required/>
            <Field label="Phone" name="mobile_no" testid="contact-phone" value={form.mobile_no} onChange={v => setForm({...form, mobile_no: v})}/>
            <Field label="Area of Interest" name="interest" testid="contact-interest" value={form.interest} onChange={v => setForm({...form, interest: v})} placeholder="e.g. GenAI, AWS, LXP"/>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-[#15348C]">Message<span className="text-[#F57C20]">*</span></label>
              <textarea required name="notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                data-testid="contact-message"
                rows={5} className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors"/>
            </div>
            <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
              <Button type="submit" disabled={status.state === "loading"} data-testid="contact-submit"
                className="bg-[#15348C] hover:bg-[#0A1F5C] text-white px-6 h-11 rounded-md disabled:opacity-60">
                {status.state === "loading"
                  ? (<><Loader2 size={16} className="mr-2 animate-spin"/> Sending…</>)
                  : (<><Send size={16} className="mr-2"/> Send Message</>)}
              </Button>
              {status.state === "success" && (
                <span data-testid="contact-success" className="text-[#4F9622] text-sm flex items-center gap-1">
                  <CheckCircle2 size={16}/> {status.message}
                </span>
              )}
              {status.state === "error" && (
                <span data-testid="contact-error" className="text-red-600 text-sm flex items-center gap-1">
                  <AlertCircle size={16}/> {status.message}
                </span>
              )}
            </div>
          </form>
        </div>
      </section>
      <Footer/>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type="text", required, placeholder, testid }) => (
  <div>
    <label className="text-sm font-semibold text-[#15348C]">{label}{required && <span className="text-[#F57C20]">*</span>}</label>
    <input type={type} name={name} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full bg-white border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#6BBE32] transition-colors"/>
  </div>
);

const InfoCard = ({ icon: Icon, title, lines }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-lg transition-all">
    <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-3">
      <Icon size={20} className="text-[#6BBE32]"/>
    </div>
    <h3 className="text-[#15348C]" style={{fontSize:"1.1rem"}}>{title}</h3>
    {lines.map(l => <p key={l} className="text-sm text-gray-600 leading-snug">{l}</p>)}
  </div>
);

export default Contact;
